import type { MouseEvent } from 'react';

export interface ContentItem {
  thumbnail_url: string;
  content_url: string;
  platform: 'YOUTUBE' | 'INSTAGRAM' | 'TIKTOK' | string;
  name: string;
}

export interface CampaignItem {
  sourceItemId: string;
  campaignUrl: string;
  imageUrl: string;
  imageAlt: string;
  titleLines: string[];
  dateText?: string;
}

const getContentKey = (item: ContentItem): string => {
  const { content_url: contentUrl, thumbnail_url: thumbnailUrl, name } = item;

  if (contentUrl) {
    const instagramMatch = contentUrl.match(
      /instagram\.com\/(?:p|reel)\/([^/?]+)/i,
    );
    if (instagramMatch) return `instagram:${instagramMatch[1]}`;

    const youtubeMatch = contentUrl.match(
      /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&?/]+)/i,
    );
    if (youtubeMatch) return `youtube:${youtubeMatch[1]}`;

    return contentUrl.split('?')[0];
  }

  return `${thumbnailUrl}:${name}`;
};

export const dedupeContentItems = (items: ContentItem[]): ContentItem[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    const key = getContentKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const dedupeCampaignItems = (items: CampaignItem[]): CampaignItem[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    const key = item.sourceItemId || item.campaignUrl;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const pickRandomItems = (
  items: ContentItem[],
  count: number,
): ContentItem[] => {
  const uniqueItems = dedupeContentItems(items);
  if (uniqueItems.length <= count) return uniqueItems;

  const shuffled = [...uniqueItems];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled.slice(0, count);
};

export const pickRandomCampaigns = (
  items: CampaignItem[],
  count: number,
): CampaignItem[] => {
  const uniqueItems = dedupeCampaignItems(items);
  if (uniqueItems.length <= count) return uniqueItems;

  const shuffled = [...uniqueItems];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled.slice(0, count);
};

const LALAFAN_ABOUT_THUMB_BASE =
  'https://attachments.lalafan.io/storage/assets/images/about';

const CREATOR_THUMB_FILES: Record<string, string> = {
  'Chloe Ellis': 'chloescloset21.jpeg',
  'Safaa Lloyd': 'safaalloydd.jpeg',
  'Marissa Ren': 'marstruck.jpeg',
  'Priscilla Kwon': 'priscillakwon.jpeg',
  Iyan: 'v1ryse.jpeg',
  'Grace Williams': 'graceellee1.jpeg',
  'Uma Detullio': 'umadetullio.jpeg',
  'Angie Yoo': 'angieyooo.jpg',
  'Amare Davis': 'itsthatsplah11.jpeg',
  'Giizele Oliveira': 'giizeleoliveira.jpg',
  'Karem Vizcarra': 'karemvizcarra.jpeg',
  'Celeste H': 'Celestehj1.jpeg',
  'Jenny Barro': 'jennybarroo.jpg',
  'Angelina Nagornova': 'justwhatyouknow.jpg',
  'Modern Nanay™': 'connhcruz.jpeg',
};

const TIKTOK_THUMB_FILES: Record<string, string> = {
  chloescloset21: 'chloescloset21.jpeg',
  safaalloydd: 'safaalloydd.jpeg',
  marstruck: 'marstruck.jpeg',
  priscillastyles: 'priscillakwon.jpeg',
  graceellee: 'graceellee1.jpeg',
  itsthatsplah11: 'itsthatsplah11.jpeg',
  karemvizcarra: 'karemvizcarra.jpeg',
  celestehj1: 'Celestehj1.jpeg',
  v1ryse: 'v1ryse.jpeg',
  umadetullio: 'umadetullio.jpeg',
  connhcruz: 'connhcruz.jpeg',
};

const INSTAGRAM_THUMB_FILES: Record<string, string> = {
  DHwWoa2SuUK: 'angieyooo.jpg',
  DKVLlUfBxOd: 'giizeleoliveira.jpg',
  DIHMEOOv5Cu: 'jennybarroo.jpg',
  DKRcQLiMG5C: 'justwhatyouknow.jpg',
};

const extractTiktokUsername = (contentUrl: string): string | null => {
  const match = contentUrl.match(/tiktok\.com\/@([^/?]+)/i);
  return match?.[1]?.toLowerCase() ?? null;
};

const extractInstagramPostId = (contentUrl: string): string | null => {
  const match = contentUrl.match(/instagram\.com\/(?:p|reel)\/([^/?]+)/i);
  return match?.[1] ?? null;
};

export const getContentThumbnailUrl = (item: ContentItem): string => {
  const creatorThumb = CREATOR_THUMB_FILES[item.name];
  if (creatorThumb) {
    return `${LALAFAN_ABOUT_THUMB_BASE}/${creatorThumb}`;
  }

  const platform = item.platform.toUpperCase();

  if (platform === 'TIKTOK') {
    const username = extractTiktokUsername(item.content_url);
    if (username) {
      const filename = TIKTOK_THUMB_FILES[username];
      if (filename) return `${LALAFAN_ABOUT_THUMB_BASE}/${filename}`;
      return `${LALAFAN_ABOUT_THUMB_BASE}/${username}.jpg`;
    }
  }

  if (platform === 'INSTAGRAM') {
    const postId = extractInstagramPostId(item.content_url);
    const filename = postId ? INSTAGRAM_THUMB_FILES[postId] : undefined;
    if (filename) return `${LALAFAN_ABOUT_THUMB_BASE}/${filename}`;
  }

  return item.thumbnail_url;
};

export const openExternalLink = (
  event: MouseEvent<HTMLAnchorElement>,
  url: string,
) => {
  if (!url) {
    event.preventDefault();
    return;
  }

  event.preventDefault();
  window.open(url, '_blank', 'noopener,noreferrer');
};
